import {
    AlertColor,
    Button,
    CircularProgress,
    FormControlLabel,
    IconButton,
    MenuItem,
    Modal,
    TextField,
    Theme,
    Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { globalTheme } from "../../../common/theme";
import createStyles from "@mui/styles/createStyles";
import axios, { AxiosResponse } from "axios";
import React, { useImperativeHandle } from "react";
import { SnackBarMessage, SnackBarMessageProps } from "../../component/SnackBarMessage";
import Title from "../../component/Title";
import { Cancel, Upload } from "@mui/icons-material";
import { DropDownComboOption } from "../../component/GenericFilter";
import { RegionCombo } from "../regions/RegionAccessories";
import { BranchInfo } from "../branches/BranchAccessories";
import { UserPreference } from "./UserAccessories";
import { getPermissionObjectNameById, PermissionObjectIds } from "../../../models/enums/PermissionObjects";
import { hasPermission, setUserPreferences } from "../../../services/AuthService";
import { UserActions } from "../../../models/enums/UserActions";
import { getDefaultPageAbbreviationById } from "../../../models/enums/DefaultPages";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
    })
);

export interface UserProps {
    onClose: Function;
}

export interface UserPreferencesHandle {
    Initialise: (preferences: UserPreference | null) => void;
}

export const UserPreferences = React.forwardRef(
    (props: UserProps, ref: React.Ref<UserPreferencesHandle>) => {
        const style = { ...useStyles(), ...globalTheme() };

        const [open, setOpen] = React.useState(false);
        const [loading, setLoading] = React.useState<boolean>(false);
        const [hasError, setHasError] = React.useState(false);
        const [regionComboData, setRegionComboData] = React.useState<DropDownComboOption[]>([]);
        const [displayName, setDisplayName] = React.useState<string>();
        const [defaultPage, setDefaultPage] = React.useState<string>("QT");
        const [defaultRegion, setDefaultRegion] = React.useState<string>("");
        const [signature, setSignature] = React.useState<any>();

        //Dialogs and snacks
        const [snackSettings, setSnackSettings] =
        React.useState<SnackBarMessageProps>({
            open: false,
            message: "",
            subMessage: "",
            severity: undefined,
            onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
        });

        function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
            setSnackSettings({
                open: true,
                message: message,
                subMessage: subMessage,
                severity: colour,
                onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
            });
        }

        useImperativeHandle(ref, () => ({
            Initialise(preferences: UserPreference | null) {
                setOpen(true);
                if (preferences)
                {
                    setDisplayName(preferences.staffName);
                    setDefaultPage(preferences.defaultPage ?? "QT");
                }

                axios.all([
                    axios.get<any[]>(`Api/V1/Region/Combo`, {
                        headers: {
                            'Content-Encoding': 'gzip'
                        }
                    }),                
                    axios.get<BranchInfo[]>(`Api/V1/Branch`, {
                        headers: {
                            'Content-Encoding': 'gzip'
                        }
                    })
                ]).then(axios.spread((...responses) => {
                    const regionComboResult: AxiosResponse<RegionCombo[]> = responses[0];
                    const branchInfoResult: AxiosResponse<BranchInfo[]> = responses[1];
        
                    let loadedRegions: DropDownComboOption[] = [];
                    branchInfoResult.data.map(b => {
                        if (regionComboResult.data.find(r => r.regionName === b.region))
                            loadedRegions.push({ name: b.companyName1, value: b.region });
                    });
                    setRegionComboData(loadedRegions);

                    if (preferences)
                    {
                        setDefaultRegion(preferences.defaultRegion);
                    }

                    axios.get(`Api/V1/User/GetAvatar`, {
                        headers: {
                            'Content-Encoding': 'gzip'
                        },
                        responseType: 'blob'
                    }).then((result) => {
                            const file = new Blob([result.data], { type: "image/png" });

                            const url = window.URL.createObjectURL(file);
            
                            setSignature(url);
                        },
                        (error) => {
                            console.log(error);
                        }
                    )
                    .catch((error) => {
                        console.log(error);
                    });  
                    setLoading(false);
                })).catch((error) => {
                    setHasError(true);
                    setLoading(false);
                    console.log(error);
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                })
            },
        }));

        function CloseUserPreferences(isCancel: boolean) {
            if (isCancel)
            {
                setOpen(false);
            }
            else
            {
                SavePreference();
                setOpen(false);
            }
        }

        function DefaultPageItem(permissionId: PermissionObjectIds) {
            if (hasPermission(permissionId, UserActions["READ"]))
            {
                let name = getPermissionObjectNameById(permissionId);
                let key = getDefaultPageAbbreviationById(permissionId);
                return(
                    <MenuItem key={key} value={key}>
                        {name}
                    </MenuItem>
                );
            }
        } 

        function uploadImage(e: React.ChangeEvent<HTMLInputElement>) {
            e.preventDefault();
            const fileList = e.target.files;
        
            if (!fileList) return;
            
            if (fileList[0]) {
                const formData = new FormData();
                formData.append("name", "image");
                formData.append("file", fileList[0]);
        
                axios.put('Api/V1/User/UploadAvatar', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    responseType: 'blob'
                })
                    .then(function (response) {
                        const file = new Blob([response.data], { type: "image/png" });

                        const url = window.URL.createObjectURL(file);
        
                        setSignature(url);
                    })
                    .catch(function (error) {
                        showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                    });

            }
        };

        function SavePreference() {
            const params = { name: displayName, defaultPage: defaultPage, defaultRegion: defaultRegion};
            axios.put('Api/V1/User/UpdatePreferences', {}, {
                params,
                headers: {
                    'Content-Type': 'application/json'
                },
            })
                .then(function (response) {
                    if (response.status === 200 )
                        setUserPreferences(displayName ?? "", defaultPage ?? "", defaultRegion ?? "");
                })
                .catch(function (error) {
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                });
        }

        return (
            <div>
                <Modal open={open} onClose={() => CloseUserPreferences(false)}>
                    <div className={style.ModalMainNarrow}>
                        {!loading ? (
                            <div className={style.ModalBody}>
                                <Title pageTitle="Preferences" />

                                <div className={style.ModalHeading}>
                                    <Typography variant="h4">Preferences</Typography>
                                </div>

                                <div className={style.ModalContent}>
                                    <div className={style.ModalFields}>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            label="Display Name"
                                            value = {displayName}
                                            onChange={(e) => setDisplayName(e.target.value)}
                                        />
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            onChange={(e) => setDefaultPage(e.target.value)}
                                            value = {defaultPage ?? "QT"}
                                            select
                                            label="Default Page"
                                        >
                                            {
                                                DefaultPageItem(PermissionObjectIds.QUOTATION)
                                            }
                                            {
                                                DefaultPageItem(PermissionObjectIds.PURCHASE_ORDER)
                                            }
                                            {
                                                DefaultPageItem(PermissionObjectIds.INVOICE)
                                            }
                                        </TextField>
                                        <TextField className={style.ModalContentFieldFullWidth}
                                            onChange={(e) => setDefaultRegion(e.target.value)}
                                            value = {defaultRegion}
                                            select
                                            label="Default Region"
                                        >
                                            {regionComboData.map((item)=> (
                                                <MenuItem key={item.value} value={item.value}>
                                                    {item.value} - {item.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <div className={style.ModalContentFieldHalfWidth}>
                                            <FormControlLabel
                                                label="Signature"
                                                control={
                                                    <img src={signature} />
                                                }
                                                labelPlacement="top"
                                            />
                                            <IconButton color="primary" aria-label="upload picture" component="label">
                                                <input hidden accept="image/*" type="file" onChange={uploadImage} />
                                                <Upload />
                                            </IconButton>
                                        </div>
                                    </div>
                                </div>

                                <div className={style.ModalFooter}>
                                    <Button
                                        variant="contained"
                                        className={style.margin}
                                        onClick={(e) => CloseUserPreferences(true)}
                                    >
                                        Cancel
                                    </Button>                                
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <CircularProgress color="primary" />
                            </div>
                        )}
                    </div>
                </Modal>

                <SnackBarMessage {...snackSettings} />
            </div>
        );


    }

);


